import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, Volume1, VolumeX, Maximize, Minimize, RotateCcw, Loader2 } from 'lucide-react';
import { videoUrl, videoPosterUrl } from '../lib/cloudinary';

const SPEEDS = [1, 1.25, 1.5, 2];
const HIDE_DELAY = 2500;

const formatTime = (s) => {
  if (!Number.isFinite(s)) return '0:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(Math.floor(s % 60)).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
};

const isTypingTarget = (el) => el?.closest?.('input, textarea, select, [contenteditable="true"]');

// Branded HTML5 player. Autoplays on mount: with sound when the browser allows it
// (e.g. after an in-site click), otherwise muted with an unmute prompt.
export const VideoPlayer = ({ video }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const hideTimer = useRef(null);
  const lastPointer = useRef('mouse');

  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [waiting, setWaiting] = useState(true);
  const [muted, setMuted] = useState(false);
  const [autoMuted, setAutoMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [scrubbing, setScrubbing] = useState(false);
  const [hover, setHover] = useState(null); // { pct, time } while hovering the track

  // Autoplay: try with sound, fall back to muted if the browser blocks it.
  useEffect(() => {
    const v = videoRef.current;
    v.muted = false;
    v.play().catch((err) => {
      if (err.name !== 'NotAllowedError') return;
      v.muted = true;
      setAutoMuted(true);
      v.play().catch(() => {}); // Fully blocked (e.g. low-power mode): user presses play.
    });
  }, []);

  const showControls = useCallback(() => {
    setControlsVisible(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (!videoRef.current?.paused) setControlsVisible(false);
    }, HIDE_DELAY);
  }, []);

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  useEffect(() => {
    if (!playing) setControlsVisible(true);
    else showControls();
  }, [playing, showControls]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (v.paused || v.ended) v.play().catch(() => {});
    else v.pause();
  }, []);

  const seekTo = useCallback((time) => {
    const v = videoRef.current;
    if (!Number.isFinite(v.duration)) return;
    v.currentTime = Math.min(Math.max(time, 0), v.duration);
    setCurrent(v.currentTime);
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    v.muted = !v.muted;
    if (!v.muted && v.volume === 0) v.volume = 1;
    setAutoMuted(false);
  }, []);

  const changeVolume = (e) => {
    const v = videoRef.current;
    v.volume = Number(e.target.value);
    v.muted = v.volume === 0;
    setAutoMuted(false);
  };

  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    videoRef.current.playbackRate = next;
    setSpeed(next);
  };

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    const v = videoRef.current;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen).call(document);
    } else if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => {});
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (v.webkitEnterFullscreen) {
      v.webkitEnterFullscreen(); // iPhone Safari: only the <video> element can go fullscreen.
    }
  }, []);

  useEffect(() => {
    const onChange = () => setFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  // Keyboard shortcuts (YouTube-style): space/k, ←/→, j/l, ↑/↓, m, f.
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      if (e.key === ' ' && e.target.closest?.('button')) return; // Native button activation handles it.
      const v = videoRef.current;
      const actions = {
        ' ': togglePlay,
        k: togglePlay,
        ArrowLeft: () => seekTo(v.currentTime - 5),
        ArrowRight: () => seekTo(v.currentTime + 5),
        j: () => seekTo(v.currentTime - 10),
        l: () => seekTo(v.currentTime + 10),
        ArrowUp: () => { v.volume = Math.min(1, v.volume + 0.1); v.muted = false; setAutoMuted(false); },
        ArrowDown: () => { v.volume = Math.max(0, v.volume - 0.1); },
        m: toggleMute,
        f: toggleFullscreen,
      };
      const action = actions[e.key];
      if (!action) return;
      e.preventDefault();
      action();
      showControls();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay, seekTo, toggleMute, toggleFullscreen, showControls]);

  // Progress-bar scrubbing.
  const ratioFromEvent = (e) => {
    const rect = trackRef.current.getBoundingClientRect();
    return Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
  };

  const onTrackPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setScrubbing(true);
    seekTo(ratioFromEvent(e) * duration);
  };

  const onTrackPointerMove = (e) => {
    const pct = ratioFromEvent(e);
    if (e.pointerType === 'mouse') setHover({ pct, time: pct * duration });
    if (scrubbing) seekTo(pct * duration);
    showControls();
  };

  const onTrackPointerUp = (e) => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    setScrubbing(false);
  };

  // Tapping the picture on touch devices reveals controls first instead of pausing.
  const onSurfaceClick = () => {
    if (lastPointer.current === 'touch' && !controlsVisible) showControls();
    else togglePlay();
  };

  const onProgress = () => {
    const v = videoRef.current;
    if (v.buffered.length && v.duration) setBuffered(v.buffered.end(v.buffered.length - 1) / v.duration);
  };

  const playedPct = duration ? (current / duration) * 100 : 0;
  const VolumeIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
  const hideChrome = playing && !controlsVisible && !scrubbing;

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden bg-black aspect-video select-none ${
        fullscreen ? '' : 'rounded-2xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]'
      } ${hideChrome ? 'cursor-none' : ''}`}
      onPointerMove={showControls}
      onPointerDown={(e) => { lastPointer.current = e.pointerType; }}
      onMouseLeave={() => playing && setControlsVisible(false)}
    >
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-contain"
        src={videoUrl(video)}
        poster={videoPosterUrl(video)}
        playsInline
        preload="auto"
        onClick={onSurfaceClick}
        onDoubleClick={toggleFullscreen}
        onPlay={() => { setPlaying(true); setEnded(false); }}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setEnded(true); }}
        onWaiting={() => setWaiting(true)}
        onPlaying={() => setWaiting(false)}
        onCanPlay={() => setWaiting(false)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onDurationChange={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => !scrubbing && setCurrent(e.currentTarget.currentTime)}
        onProgress={onProgress}
        onVolumeChange={(e) => { setMuted(e.currentTarget.muted); setVolume(e.currentTarget.volume); }}
      />

      {/* Centre state: buffering spinner, big play, or replay */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {waiting && playing ? (
          <Loader2 size={44} className="text-white/80 animate-spin" />
        ) : !playing ? (
          <button
            onClick={togglePlay}
            aria-label={ended ? 'Replay' : 'Play'}
            className="pointer-events-auto w-12 h-12 sm:w-20 sm:h-20 rounded-full bg-[#F58220] hover:bg-[#e07316] hover:scale-105 text-white flex items-center justify-center shadow-[0_10px_40px_rgba(245,130,32,0.45)] transition"
          >
            {ended ? <RotateCcw className="w-5 h-5 sm:w-7 sm:h-7" /> : <Play fill="currentColor" className="w-5 h-5 sm:w-7 sm:h-7 ml-0.5 sm:ml-1" />}
          </button>
        ) : null}
      </div>

      {/* Muted-autoplay prompt */}
      {autoMuted && muted && (
        <button
          onClick={toggleMute}
          className="absolute top-3 left-3 sm:top-4 sm:left-4 inline-flex items-center gap-2 bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/15 text-white text-[12px] sm:text-[13px] font-semibold px-3.5 py-2 rounded-full transition"
        >
          <VolumeX size={15} className="text-[#F58220]" /> Tap to unmute
        </button>
      )}

      {/* Control bar */}
      <div
        className={`absolute inset-x-0 bottom-0 pt-10 sm:pt-16 pb-1 sm:pb-3 px-2 sm:px-5 bg-gradient-to-t from-black/85 via-black/40 to-transparent transition-opacity duration-300 ${
          hideChrome ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        {/* Progress track */}
        <div
          ref={trackRef}
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(current)}
          aria-valuetext={`${formatTime(current)} of ${formatTime(duration)}`}
          className="group/track relative h-5 flex items-center cursor-pointer touch-none"
          onPointerDown={onTrackPointerDown}
          onPointerMove={onTrackPointerMove}
          onPointerUp={onTrackPointerUp}
          onPointerLeave={() => setHover(null)}
        >
          <div className={`relative w-full rounded-full bg-white/20 transition-all ${scrubbing ? 'h-1.5' : 'h-1 group-hover/track:h-1.5'}`}>
            <div className="absolute inset-y-0 left-0 rounded-full bg-white/30" style={{ width: `${buffered * 100}%` }} />
            {hover && <div className="absolute inset-y-0 left-0 rounded-full bg-white/20" style={{ width: `${hover.pct * 100}%` }} />}
            <div className="absolute inset-y-0 left-0 rounded-full bg-[#F58220]" style={{ width: `${playedPct}%` }} />
            <div
              className={`absolute top-1/2 w-3.5 h-3.5 -ml-[7px] -mt-[7px] rounded-full bg-[#F58220] ring-2 ring-white/90 shadow transition-transform ${
                scrubbing ? 'scale-100' : 'scale-0 group-hover/track:scale-100'
              }`}
              style={{ left: `${playedPct}%` }}
            />
          </div>
          {hover && (
            <div
              className="absolute bottom-6 -translate-x-1/2 px-2 py-1 rounded-md bg-black/80 border border-white/10 text-[11px] font-mono text-white pointer-events-none"
              style={{ left: `${hover.pct * 100}%` }}
            >
              {formatTime(hover.time)}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="sm:mt-1 flex items-center gap-0.5 sm:gap-2 text-white">
          <button onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'} className="p-1.5 sm:p-2 rounded-full hover:bg-white/10 transition">
            {playing ? <Pause className="w-[18px] h-[18px] sm:w-5 sm:h-5" fill="currentColor" /> : ended ? <RotateCcw className="w-[18px] h-[18px] sm:w-5 sm:h-5" /> : <Play className="w-[18px] h-[18px] sm:w-5 sm:h-5" fill="currentColor" />}
          </button>

          <div className="group/vol flex items-center">
            <button onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'} className="p-1.5 sm:p-2 rounded-full hover:bg-white/10 transition">
              <VolumeIcon className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={muted ? 0 : volume}
              onChange={changeVolume}
              aria-label="Volume"
              className="hidden sm:block w-0 opacity-0 group-hover/vol:w-20 group-hover/vol:opacity-100 focus:w-20 focus:opacity-100 transition-all duration-200 accent-[#F58220] cursor-pointer"
            />
          </div>

          <span className="ml-1 whitespace-nowrap text-[11px] sm:text-[13px] font-mono text-white/85 tabular-nums">
            {formatTime(current)} <span className="text-white/40">/ {formatTime(duration)}</span>
          </span>

          <div className="ml-auto flex items-center gap-0.5 sm:gap-2">
            <button
              onClick={cycleSpeed}
              aria-label="Playback speed"
              className="min-w-[40px] px-1.5 sm:px-2 py-1 rounded-md text-[11px] sm:text-[12px] font-mono font-semibold hover:bg-white/10 transition"
            >
              {speed}×
            </button>
            <button onClick={toggleFullscreen} aria-label={fullscreen ? 'Exit full screen' : 'Full screen'} className="p-1.5 sm:p-2 rounded-full hover:bg-white/10 transition">
              {fullscreen ? <Minimize className="w-[18px] h-[18px] sm:w-5 sm:h-5" /> : <Maximize className="w-[18px] h-[18px] sm:w-5 sm:h-5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
